import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import fvLogo from "../../../../Assets/image/fvLogo.svg";
import QuotationTable from "./QuotationTable";
import Icons from "../../../../Assets/Icons";
import ToastPopup from "../../../../widgets/Toast/toastPopup.js";
import { uploadMedia } from "../../../../modules/patientProfile/patientProfileThunk";
import { extractUploadedFileUrl, getFileNameFromUrl } from "../../../../utils/fileHelpers";
import { getLocalizedTitle } from "../../../../utils/localizedText";
import { openSecureFileUrl } from "../../../../utils/secureFilePreview";
import { calculateQuotationTotal } from "../../../../utils/quotationPricing";
import { fetchTransportationFee } from "../../../../modules/NursignCare/TransportationFee/transportationFeeThunk";
import { generateQuotationPdf } from "../../../../utils/quotationPdf/quotationPdfGenerator";
import { buildQuotationFileName } from "../../../../utils/quotationFileName";
import "./QuotationDetails.css";

// Picks the catalog fee tier matching the patient's insurance category (Subsidized patients get
// price.wh.opdSubsidized rather than the International/Local tiers) instead of always defaulting
// to International.
const pickCatalogFee = (price, insuranceCategory) => {
  const wh = price?.wh || {};
  const category = (insuranceCategory || "").toLowerCase();
  if (category.includes("subsidized")) return parseFloat(wh.opdSubsidized || 0);
  if (category.includes("local")) return parseFloat(wh.opdLocalPrice || 0);
  return parseFloat(wh.opdInternationalPrice || wh.opdLocalPrice || 0);
};

// Prefills from the matching persisted service (by index — visits/services are 1:1 index-aligned
// throughout this flow) when the backend already has per_time/serviceFee/transportFee/additionalFee
// for it, instead of leaving the fields blank.
const buildItemsFromVisits = (visits, distanceInKm, persistedServices = [], insuranceCategory = "") => {
  return (visits || []).map((v, index) => {
    const persisted = persistedServices[index];
    const price = v.serviceItemData?.price;
    const catalogFee = pickCatalogFee(price, insuranceCategory);
    const dateRange = v.isMultipleDay ? `${v.startDate} - ${v.endDate}` : v.singleDate || "";
    const serviceItemTitle = v.serviceItemData?.title || v.serviceItemTitle || v.serviceItemName;

    return {
      serviceName: getLocalizedTitle(serviceItemTitle) || "-",
      serviceItemTitle,
      serviceItemData: v.serviceItemData || null,
      dateRange,
      startTime: v.startTime || "",
      endTime: v.endTime || "",
      distanceInKm,
      unitFee: persisted?.serviceFee ?? catalogFee,
      perTime: persisted?.per_time ?? 1,
      transportationFee: persisted?.transportFee ?? "",
      adjustment: persisted?.additionalFee ?? "",
    };
  });
};

const QuotationDetails = ({ setPage, visitsData, distanceInKm, existingQuotationURL, persistedServices, quotationPdfData, insuranceCategory, onFinalSubmit }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const transportationFeeConfig = useSelector((state) => state.transportationFee?.fee);

  const [quotationItems, setQuotationItems] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [quotationURL, setQuotationURL] = useState("");
  const [isUploadingQuotation, setIsUploadingQuotation] = useState(false);

  useEffect(() => {
    if (visitsData?.length) {
      setQuotationItems(buildItemsFromVisits(visitsData, distanceInKm, persistedServices, insuranceCategory));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitsData, distanceInKm, insuranceCategory]);

  useEffect(() => {
    if (!transportationFeeConfig) dispatch(fetchTransportationFee());
  }, [dispatch, transportationFeeConfig]);

  // Hydrate from a previously uploaded quotation so it can be viewed/replaced instead of looking empty
  useEffect(() => {
    if (existingQuotationURL && !pdfFile) {
      setQuotationURL(existingQuotationURL);
    }
  }, [existingQuotationURL]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnitFeeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, unitFee: value } : item)));
  };

  const handlePerTimeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, perTime: value } : item)));
  };

  const handleTransportationFeeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, transportationFee: value } : item)));
  };

  const handleAdjustmentChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, adjustment: value } : item)));
  };

  useEffect(() => {
    return () => {
      if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    };
  }, [pdfPreview]);

  const setSelectedQuotationFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      ToastPopup.error(t("pleaseUploadPdfOnly"));
      return;
    }

    if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    const localPreviewUrl = URL.createObjectURL(file);
    setPdfFile(file);
    setPdfPreview(localPreviewUrl);
    setQuotationURL("");
    setIsUploadingQuotation(true);

    try {
      const result = await dispatch(uploadMedia({ files: [file] }));
      if (result.meta.requestStatus !== "fulfilled") {
        throw new Error(result.error?.message || "Upload failed");
      }

      const uploadedUrl = extractUploadedFileUrl(result.payload);
      if (!uploadedUrl) {
        throw new Error("Upload completed without a file URL");
      }

      setQuotationURL(uploadedUrl);
    } catch (error) {
      URL.revokeObjectURL(localPreviewUrl);
      setPdfFile(null);
      setPdfPreview(null);
      setQuotationURL("");
      ToastPopup.error(error.message || t("bulkUploadFailedGeneric") || "Upload failed");
    } finally {
      setIsUploadingQuotation(false);
    }
  };

  const handleFileUpload = (e) => {
    setSelectedQuotationFile(e.target.files[0]);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setSelectedQuotationFile(e.dataTransfer.files[0]);
  };

  const handleRemovePdf = () => {
    if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    setPdfFile(null);
    setPdfPreview(null);
    setQuotationURL("");
  };

  const handleSendQuote = () => {
    if (isUploadingQuotation) {
      ToastPopup.error(t("loading") || "Please wait for upload to finish");
      return;
    }
    const uploadedQuotationURL = quotationURL.trim() || null;
    // Total per service = (serviceFee × per_time) + transportFee + additionalFee, summed across services
    const total = calculateQuotationTotal(
      quotationItems.map((item) => ({ serviceFee: item.unitFee, transportFee: item.transportationFee, additionalFee: item.adjustment, perTime: item.perTime })),
    );
    const localizedQuotationItems = quotationItems.map((item) => ({
      ...item,
      serviceName: getLocalizedTitle(item.serviceItemData || item.serviceItemTitle, i18n.language) || item.serviceName,
      // Canonical field names expected by the backend's itemized services[] payload
      per_time: item.perTime,
      serviceFee: item.unitFee,
      transportFee: item.transportationFee,
      additionalFee: item.adjustment,
    }));
    onFinalSubmit?.({ quotationURL: uploadedQuotationURL, price: String(total), quotationItems: localizedQuotationItems });
    ToastPopup.success(t("quoteSent"));
  };

  const handleDownloadQuotation = async () => {
    const patient = quotationPdfData?.patient || {};
    const request = quotationPdfData?.request || {};
    const consultantName = quotationPdfData?.consultantName || "FV Healthcare at Home";
    const relationship = quotationPdfData?.relationship || "";

    // Format quotation items for the PDF generator
    const formattedQuotationItems = quotationItems.map((item) => ({
      ...item,
      serviceItemData: item.serviceItemData,
      serviceItemTitle: item.serviceItemTitle,
      serviceName: item.serviceName,
    }));

    const fileName = buildQuotationFileName(patient.name, patient.hnNumber) || `quotation-${request.requestId || "details"}.pdf`;

    try {
      await generateQuotationPdf({
        logoSrc: fvLogo,
        patient,
        request,
        quotationItems: formattedQuotationItems,
        transportationFeeConfig,
        consultantName,
        relationship,
        fileName,
      });
      ToastPopup.success(t("quotationDownloaded") || "Quotation downloaded successfully");
    } catch (error) {
      console.error("Error generating quotation PDF:", error);
      ToastPopup.error(t("quotationDownloadError") || "Failed to download quotation");
    }
  };


  return (
    <div className="quotation-details-container">
      {/* Quotation Table */}
      <div className="whp-container quotation-table-card">
        <div className="title-hr2 quotation-title-row">
          <div className="quotation-title-with-action">
            <p>{t("quotationDetails")}</p>
            <button type="button" className="download-quotation-btn" onClick={handleDownloadQuotation} disabled={isUploadingQuotation}>
              {t("downloadQuotation")}
            </button>
          </div>
          <hr />
        </div>
        <QuotationTable
          items={quotationItems}
          onUnitFeeChange={handleUnitFeeChange}
          onTransportationFeeChange={handleTransportationFeeChange}
          onAdjustmentChange={handleAdjustmentChange}
          onPerTimeChange={handlePerTimeChange}
        />
      </div>

      {/* Upload Quotation */}
      <div className="whp-container pdf-upload-card">
        <div className="title-hr2">
          <p>{t("uploadQuotationDocument")}</p>
          <hr />
        </div>
        <div className="pdf-upload-section">
          {!pdfFile && !quotationURL ? (
            <div className="pdf-upload-area" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
              <input type="file" accept="application/pdf" onChange={handleFileUpload} id="pdf-upload-input" className="pdf-upload-input" />
              <label htmlFor="pdf-upload-input" className="pdf-upload-label">
                <img src={Icons.upload} alt="upload" className="pdf-icon-small" />
                <span className="pdf-upload-text">{t("pleaseUploadQuotationPdf")}</span>
                <span className="pdf-upload-hint">{t("or") || "Or"}</span>
                <span className="pdf-browse-btn">{t("browseFiles") || "Browse files"}</span>
              </label>
            </div>
          ) : (
            <div className="pdf-preview-area">
              <div className="pdf-preview-info">
                <img src={Icons.upload} alt="PDF" className="pdf-icon-small" />
                <div className="pdf-preview-details">
                  <span className="pdf-file-name">{pdfFile ? pdfFile.name : getFileNameFromUrl(quotationURL) || t("quotationDetails")}</span>
                  {pdfFile && <span className="pdf-file-size">{(pdfFile.size / 1024).toFixed(2)} KB</span>}
                </div>
              </div>
              <div className="pdf-preview-actions">
                <button className="pdf-view-btn" onClick={() => openSecureFileUrl(quotationURL || pdfPreview)} disabled={isUploadingQuotation}>
                  {isUploadingQuotation ? t("loading") : t("view")}
                </button>
                <input type="file" accept="application/pdf" onChange={handleFileUpload} id="pdf-replace-input" className="pdf-upload-input" />
                <label htmlFor="pdf-replace-input" className="pdf-view-btn">
                  {t("replace") || "Replace"}
                </label>
                <button className="pdf-remove-btn" onClick={handleRemovePdf}>
                  <img src={Icons.trashIcon} alt="Remove" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={() => setPage(3)}>
          {t("back")}
        </button>
        <button className="form-section-create send-quote-btn" onClick={handleSendQuote}>
          {t("sendQuote")}
        </button>
      </div>
    </div>
  );
};

export default QuotationDetails;
